import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Upload, Download, Trash2, FileText, Image, File, Paperclip, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';

const FILE_ICONS = {
  'application/pdf': { icon: FileText, color: 'text-red-500' },
  'image/jpeg': { icon: Image, color: 'text-blue-500' },
  'image/png': { icon: Image, color: 'text-blue-500' },
  'image/webp': { icon: Image, color: 'text-blue-500' },
};

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ClientAttachmentsTab = ({ clientId }) => {
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${clientId}/attachments`);
      setAttachments(res.data);
    } catch { /* ignore */ }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(`${API}/clients/${clientId}/attachments`, formData);
      }
      toast.success(`${files.length} fichier(s) ajoute(s)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (attId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await axios.delete(`${API}/clients/${clientId}/attachments/${attId}`);
      toast.success('Supprime');
      load();
    } catch (err) {
      toast.error('Erreur suppression');
    }
  };

  const handleDownload = (att) => {
    window.open(`${API}/clients/${clientId}/attachments/${att.id}/download`, '_blank');
  };

  return (
    <div className="space-y-4" data-testid="attachments-tab">
      {/* Upload zone */}
      <Card className="p-4 rounded-xl">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-slate-900">Documents et pieces jointes</h3>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
              className="hidden"
              onChange={handleUpload}
              data-testid="file-upload-input"
            />
            <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              Ajouter un document
            </Button>
          </div>
        </div>

        {attachments.length === 0 ? (
          <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center" data-testid="empty-attachments">
            <Paperclip className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            <p className="text-sm text-slate-400">Aucun document</p>
            <p className="text-xs text-slate-300 mt-1">Photos, PDF, devis, factures, contrats...</p>
          </div>
        ) : (
          <div className="space-y-2">
            {attachments.map(att => {
              const fi = FILE_ICONS[att.content_type] || { icon: File, color: 'text-slate-500' };
              const Icon = fi.icon;
              const isImage = att.content_type?.startsWith('image/');
              return (
                <div key={att.id} className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors" data-testid={`attachment-${att.id}`}>
                  {isImage ? (
                    <img
                      src={`${API}/clients/${clientId}/attachments/${att.id}/download`}
                      alt={att.filename}
                      className="w-10 h-10 rounded object-cover border border-slate-200 shrink-0 cursor-pointer"
                      onClick={() => handleDownload(att)}
                    />
                  ) : (
                    <div className={`w-10 h-10 rounded bg-white border border-slate-200 flex items-center justify-center shrink-0 ${fi.color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{att.filename}</p>
                    <div className="flex items-center gap-2 text-[10px] text-slate-400">
                      <span>{formatSize(att.size)}</span>
                      <span>{att.uploaded_at?.slice(0, 10)}</span>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => handleDownload(att)} title="Telecharger" data-testid={`download-${att.id}`}>
                      <Download className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(att.id)} title="Supprimer" data-testid={`delete-${att.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default ClientAttachmentsTab;
